/**
 * Sentry frontend — DEBE importarse el primero en src/index.js.
 *
 * Activación 100% por entorno: sin REACT_APP_SENTRY_DSN el archivo es un
 * no-op (log claro en consola, cero impacto en la app).
 *
 * Privacidad estricta (requisito de cliente):
 *  - Session Replay con maskAllText + blockAllMedia + maskAllInputs.
 *  - sendDefaultPii: false; beforeSend elimina cookies y headers de auth.
 *  - Sampling conservador configurable por env.
 *
 * Correlación con backend: tracePropagationTargets añade sentry-trace/baggage
 * a las llamadas del dominio del API (el backend continúa la traza).
 */
import * as Sentry from "@sentry/react";

const dsn = (process.env.REACT_APP_SENTRY_DSN || "").trim();

const buildTracePropagationTargets = () => {
  const targets = [/^\/api\//];
  try {
    const host = new URL(process.env.REACT_APP_BACKEND_URL).host;
    targets.push(new RegExp(`^https?://${host.replace(/\./g, "\\.")}`));
  } catch {
    /* sin backend URL válida: solo rutas relativas /api */
  }
  return targets;
};

if (dsn) {
  // El environment se deriva del hostname REAL en runtime: la variable de
  // build (REACT_APP_SENTRY_ENVIRONMENT) quedaba horneada como "preview" en
  // los builds desplegados a producción y contaminaba los filtros de Sentry.
  const inferEnvironment = () => {
    try {
      const host = window.location.hostname;
      if (host === "localhost" || host === "127.0.0.1") return "development";
      if (host.endsWith(".preview.emergentagent.com")) return "preview";
      return "production";
    } catch {
      return process.env.REACT_APP_SENTRY_ENVIRONMENT || "development";
    }
  };
  const environment = inferEnvironment();
  const release = process.env.REACT_APP_SENTRY_RELEASE || undefined;

  Sentry.init({
    dsn,
    environment,
    release,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
      }),
    ],
    tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0.1"),
    tracePropagationTargets: buildTracePropagationTargets(),
    replaysSessionSampleRate: parseFloat(process.env.REACT_APP_SENTRY_REPLAYS_SESSION_RATE || "0.01"),
    replaysOnErrorSampleRate: parseFloat(process.env.REACT_APP_SENTRY_REPLAYS_ERROR_RATE || "1.0"),
    beforeSend(event) {
      if (event.request) {
        delete event.request.cookies;
        if (event.request.headers) {
          delete event.request.headers.Authorization;
          delete event.request.headers.authorization;
          delete event.request.headers.Cookie;
          delete event.request.headers.cookie;
        }
      }
      return event;
    },
  });
  // eslint-disable-next-line no-console
  console.info(`[sentry] frontend activo (env=${environment}, release=${release || "n/a"})`);
} else {
  // eslint-disable-next-line no-console
  console.info("[sentry] desactivado: REACT_APP_SENTRY_DSN no configurado");
}
